import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
  LuxonDateAdapter,
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  MatLuxonDateAdapterOptions,
} from '@angular/material-luxon-adapter';
import { DateTime } from 'luxon';
import { DateTimeService } from './date-time.service';
import { CONFIG } from '../../config';

/**
 * A digits-only entry, e.g. `19` or `1930`. `DateTime.fromISO` reads these as a bare hour or a
 * bare year, so without this guard typing `19` silently resolved to today at 19:00 and `1930` to
 * January 1st of 1930 (RA-2738).
 */
const DIGITS_ONLY = /^\d+$/;

/**
 * A slash- or dash-separated date, capturing the year segment. Used to reject years that are
 * neither two nor four digits: Luxon's `yy` token matches two to four digits and returns anything
 * above 99 verbatim, so `5/1/269` parsed as the year 269 instead of failing (RA-2738). The
 * leading segment is capped at two digits so genuine `yyyy-MM-dd` input is not caught here.
 */
const SEPARATED_DATE_YEAR = /^\d{1,2}([/-])\d{1,2}\1(\d+)$/;

/**
 * Extends the native Angular Material Luxon adapter so that `parse()` will
 * parse the date using our own logic.
 */
@Injectable()
export class MultiFormatLuxonDateAdapter extends LuxonDateAdapter {
  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string | null,
    @Optional() @Inject(MAT_LUXON_DATE_ADAPTER_OPTIONS) options?: MatLuxonDateAdapterOptions
  ) {
    super(dateLocale, options);
  }

  override parse(value: unknown, parseFormat: string | Array<string> | null | undefined): DateTime | null {
    if (!value || value === '') {
      return null;
    }

    // Delegate to the default behavior when it's not a string
    if (typeof value !== 'string') {
      return super.parse(value, Array.isArray(parseFormat) ? parseFormat[0] : parseFormat);
    }

    const trimmed = value.trim();

    if (this.isMalformedEntry(trimmed)) {
      return null;
    }

    const parsed = DateTimeService.parseDate(trimmed, CONFIG.formatEST, parseFormat);

    return parsed.isValid ? parsed : null;
  }

  /**
   * Rejects the two shapes of typed input that the underlying parsers accept but a user never
   * means: a bare run of digits, and a separated date whose year is neither two nor four digits.
   * Both used to produce a plausible-looking but wrong date rather than an error (RA-2738).
   */
  private isMalformedEntry(value: string): boolean {
    if (DIGITS_ONLY.test(value)) {
      return true;
    }

    const yearSegment = SEPARATED_DATE_YEAR.exec(value)?.[2];

    return yearSegment !== undefined && yearSegment.length !== 2 && yearSegment.length !== 4;
  }
}
